export type QuarantineQuotaRequestRow = {
  id: string;
  quota_request_key: string;
  deployment_environment_name: string;
  aws_region: string;
  quota_name: string;
  status: string;
  aws_status: string;
  requested_value?: number;
  service_code?: string;
  quota_code?: string;
  requested_at_timestamp?: number;
  created_at_timestamp?: number;
  last_message_at_timestamp?: number;
  priority?: string;
  project_id?: string;
  aws_account_id?: string;
  aws_support_case_id?: string;
};

export type SupportCaseConsoleUrlInput = {
  aws_account_id?: string;
  aws_support_case_id?: string;
};

/** AWS Support console deep link for a quota request row, or null when not linkable. */
export function buildSupportCaseConsoleUrl(
  quotaRequestRow: SupportCaseConsoleUrlInput | null,
): string | null {
  if (quotaRequestRow === null) {
    return null;
  }

  const awsAccountId = quotaRequestRow.aws_account_id ?? "";

  if (awsAccountId === "") {
    return null;
  }

  const supportConsoleHost =
    "https://{aws_account_id}.support.console.aws.amazon.com/support/home#/case/".replace(
      "{aws_account_id}",
      encodeURIComponent(awsAccountId),
    );

  const awsSupportCaseId = quotaRequestRow.aws_support_case_id ?? "";

  if (awsSupportCaseId !== "") {
    return `${supportConsoleHost}?displayId=${encodeURIComponent(awsSupportCaseId)}`;
  }

  return null;
}

export const AWS_SERVICE_QUOTA_STATUS_PENDING = "PENDING";

/** True when Service Quotas aws_status implies a Support case should be linked. */
export function awsStatusExpectsSupportCaseId(awsStatus: string): boolean {
  if (awsStatus === "" || awsStatus === AWS_SERVICE_QUOTA_STATUS_PENDING) {
    return false;
  }

  return true;
}

/** True when the row should have a Support case id but does not. */
export function isSupportCaseMissingForRow(row: QuarantineQuotaRequestRow): boolean {
  if (!awsStatusExpectsSupportCaseId(row.aws_status)) {
    return false;
  }

  if (row.aws_support_case_id === undefined || row.aws_support_case_id === "") {
    return true;
  }

  return false;
}

export const SUPPORT_CASE_MISSING_LABEL = "No support case linked";

export type ProjectOption = {
  id: string;
  name: string;
  created_at_timestamp?: number;
};

export const WORKFLOW_STATUS_QUARANTINE = "quarantine";
export const WORKFLOW_STATUS_TRACKED = "tracked";
export const WORKFLOW_STATUS_MUST_RESPOND = "must_respond";
export const WORKFLOW_STATUS_LIMBO = "limbo";
export const WORKFLOW_STATUS_CLOSED = "closed";

export type QuotaRequestInboxSegment = "quarantine" | "pending";

/** Keeps rows for the Pending (non-quarantine) or Quarantine inbox segment. */
export function filterQuotaRequestRowsByInboxSegment(
  rows: QuarantineQuotaRequestRow[],
  segment: QuotaRequestInboxSegment,
): QuarantineQuotaRequestRow[] {
  if (segment === "quarantine") {
    return rows.filter((row) => row.status === WORKFLOW_STATUS_QUARANTINE);
  }

  return rows.filter((row) => row.status !== WORKFLOW_STATUS_QUARANTINE);
}

/** Default inbox segment: quarantine when any quarantine row exists, otherwise pending. */
export function resolveDefaultInboxSegment(rows: QuarantineQuotaRequestRow[]): QuotaRequestInboxSegment {
  for (const row of rows) {
    if (row.status === WORKFLOW_STATUS_QUARANTINE) {
      return "quarantine";
    }
  }

  return "pending";
}

export const ASSIGNED_WORKFLOW_STATUS_OPTIONS = [
  WORKFLOW_STATUS_TRACKED,
  WORKFLOW_STATUS_MUST_RESPOND,
  WORKFLOW_STATUS_LIMBO,
  WORKFLOW_STATUS_CLOSED,
] as const;

export const QUOTA_REQUEST_DEFAULT_PRIORITY = "medium";

export const QUOTA_REQUEST_PRIORITY_OPTIONS = [
  "low",
  "medium",
  "high",
  "critical",
] as const;

export const PROJECT_SELECT_UNASSIGNED_VALUE = "";
export const PROJECT_SELECT_NEW_PROJECT_VALUE = "__new_project__";
export const PROJECT_DISPLAY_UNASSIGNED_LABEL = "Unassigned";
export const PROJECT_SELECT_NEW_PROJECT_LABEL = "New project…";

export function resolveRequestedAtEpochSeconds(row: QuarantineQuotaRequestRow): number | undefined {
  if (row.requested_at_timestamp !== undefined) {
    return row.requested_at_timestamp;
  }

  return row.created_at_timestamp;
}

export function formatQuotaRequestAccountRegionHeaderLine(
  awsAccountId: string | undefined,
  deploymentEnvironmentName: string | undefined,
  awsRegion: string | undefined,
): string {
  const accountIdText = awsAccountId ?? "—";
  const accountNameText = deploymentEnvironmentName ?? "—";
  const regionText = awsRegion ?? "—";

  return `${accountIdText} (${accountNameText}) | ${regionText}`;
}

export function formatRequestedAtTimestamp(epochSeconds: number | undefined): string {
  if (epochSeconds === undefined) {
    return "";
  }

  const dateValue = new Date(epochSeconds * 1000);
  const datePart = dateValue.toLocaleDateString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const timePart = dateValue.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  });

  return `${datePart} ${timePart}`;
}

export function formatRequestedAtDateOnly(epochSeconds: number | undefined): string {
  if (epochSeconds === undefined) {
    return "";
  }

  const dateValue = new Date(epochSeconds * 1000);

  return dateValue.toLocaleDateString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

const RELATIVE_TIME_DIVISIONS: { amount: number; unit: Intl.RelativeTimeFormatUnit }[] = [
  { amount: 60, unit: "second" },
  { amount: 60, unit: "minute" },
  { amount: 24, unit: "hour" },
  { amount: 7, unit: "day" },
  { amount: 4.34524, unit: "week" },
  { amount: 12, unit: "month" },
  { amount: Number.POSITIVE_INFINITY, unit: "year" },
];

export function formatLastMessageAtRelativePhrase(epochSeconds: number | undefined): string {
  if (epochSeconds === undefined) {
    return "—";
  }

  const nowMilliseconds = Date.now();
  const messageMilliseconds = epochSeconds * 1000;
  let durationSeconds = (messageMilliseconds - nowMilliseconds) / 1000;
  const relativeTimeFormatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

  for (const division of RELATIVE_TIME_DIVISIONS) {
    if (Math.abs(durationSeconds) < division.amount) {
      return relativeTimeFormatter.format(Math.round(durationSeconds), division.unit);
    }

    durationSeconds /= division.amount;
  }

  return "—";
}

export type QuarantineQuotaRequestSortColumn =
  | "requested_at"
  | "deployment_environment_name"
  | "aws_region"
  | "quota_name"
  | "aws_status"
  | "requested_value"
  | "project"
  | "status"
  | "priority";

export type QuarantineQuotaRequestSortDirection = "asc" | "desc";

export function resolveQuotaNameForRow(row: QuarantineQuotaRequestRow): string {
  if (row.quota_name) {
    return row.quota_name;
  }

  if (row.service_code) {
    return row.service_code;
  }

  return "";
}

export function resolveQuotaNameDisplayForRow(row: QuarantineQuotaRequestRow): string {
  const quotaName = resolveQuotaNameForRow(row);

  if (quotaName) {
    return quotaName;
  }

  return "—";
}

export function resolvePriorityDisplayForRow(row: QuarantineQuotaRequestRow): string {
  if (row.priority !== undefined) {
    return row.priority;
  }

  return "—";
}

export function resolveProjectSelectValue(row: QuarantineQuotaRequestRow): string {
  if (row.project_id !== undefined && row.project_id !== "") {
    return row.project_id;
  }

  return PROJECT_SELECT_UNASSIGNED_VALUE;
}

export function shouldShowUnassignedProjectOption(workflowStatus: string): boolean {
  return (
    workflowStatus === WORKFLOW_STATUS_QUARANTINE || workflowStatus === WORKFLOW_STATUS_TRACKED
  );
}

export function shouldShowNewProjectOption(workflowStatus: string): boolean {
  return shouldShowUnassignedProjectOption(workflowStatus);
}

export function formatProjectOptionChoiceLabel(projectOption: ProjectOption): string {
  const datePrefix = formatRequestedAtDateOnly(projectOption.created_at_timestamp);

  if (datePrefix === "") {
    return projectOption.name;
  }

  return `${datePrefix} ${projectOption.name}`;
}

export function buildSortedProjectOptions(projectOptionList: ProjectOption[]): ProjectOption[] {
  const projectOptionCopy = [...projectOptionList];

  projectOptionCopy.sort((left, right) => {
    const leftCreatedAtTimestamp = left.created_at_timestamp ?? 0;
    const rightCreatedAtTimestamp = right.created_at_timestamp ?? 0;

    if (rightCreatedAtTimestamp !== leftCreatedAtTimestamp) {
      return rightCreatedAtTimestamp - leftCreatedAtTimestamp;
    }

    return left.name.localeCompare(right.name);
  });

  return projectOptionCopy;
}

export function formatProjectFilterOptionLabel(
  projectFilterValue: string,
  registeredProjectOptions: ProjectOption[],
  projectNameById: QuarantineQuotaRequestProjectNameById,
): string {
  if (projectFilterValue === PROJECT_SELECT_UNASSIGNED_VALUE) {
    return PROJECT_DISPLAY_UNASSIGNED_LABEL;
  }

  const registeredProjectOption = registeredProjectOptions.find(
    (projectOption) => projectOption.id === projectFilterValue,
  );

  if (registeredProjectOption !== undefined) {
    return formatProjectOptionChoiceLabel(registeredProjectOption);
  }

  const projectName = projectNameById[projectFilterValue];

  if (projectName !== undefined) {
    return formatProjectOptionChoiceLabel({
      id: projectFilterValue,
      name: projectName,
    });
  }

  return projectFilterValue;
}

export function resolveProjectDisplayName(
  row: QuarantineQuotaRequestRow,
  projectNameById: Record<string, string>,
): string {
  const projectId = resolveProjectSelectValue(row);

  if (projectId === PROJECT_SELECT_UNASSIGNED_VALUE) {
    return PROJECT_DISPLAY_UNASSIGNED_LABEL;
  }

  const projectName = projectNameById[projectId];

  if (projectName !== undefined) {
    return projectName;
  }

  return projectId;
}

export type QuarantineQuotaRequestFilterColumn = Exclude<
  QuarantineQuotaRequestSortColumn,
  "requested_at" | "requested_value"
>;

export const QUARANTINE_QUOTA_REQUEST_FILTER_COLUMNS: QuarantineQuotaRequestFilterColumn[] = [
  "deployment_environment_name",
  "aws_region",
  "quota_name",
  "aws_status",
  "project",
  "status",
  "priority",
];

export type QuarantineQuotaRequestProjectNameById = Record<string, string>;

export type QuarantineQuotaRequestFilterState = Partial<
  Record<QuarantineQuotaRequestFilterColumn, string>
>;

export function getQuarantineQuotaRequestFilterCellValue(
  column: QuarantineQuotaRequestFilterColumn,
  row: QuarantineQuotaRequestRow,
  projectNameById: QuarantineQuotaRequestProjectNameById,
): string {
  switch (column) {
    case "deployment_environment_name":
      return row.deployment_environment_name;
    case "aws_region":
      return row.aws_region;
    case "quota_name":
      return resolveQuotaNameDisplayForRow(row);
    case "aws_status":
      return row.aws_status;
    case "project":
      return resolveProjectSelectValue(row);
    case "status":
      return row.status;
    case "priority":
      return resolvePriorityDisplayForRow(row);
    default:
      return "";
  }
}

export function rowMatchesQuarantineQuotaRequestFilters(
  row: QuarantineQuotaRequestRow,
  filterState: QuarantineQuotaRequestFilterState,
  projectNameById: QuarantineQuotaRequestProjectNameById,
): boolean {
  for (const column of QUARANTINE_QUOTA_REQUEST_FILTER_COLUMNS) {
    const selectedValue = filterState[column];

    if (selectedValue === undefined || selectedValue === "") {
      continue;
    }

    if (getQuarantineQuotaRequestFilterCellValue(column, row, projectNameById) !== selectedValue) {
      return false;
    }
  }

  return true;
}

export function filterQuarantineQuotaRequestRows(
  rows: QuarantineQuotaRequestRow[],
  filterState: QuarantineQuotaRequestFilterState,
  projectNameById: QuarantineQuotaRequestProjectNameById,
): QuarantineQuotaRequestRow[] {
  return rows.filter((row) =>
    rowMatchesQuarantineQuotaRequestFilters(row, filterState, projectNameById),
  );
}

function collectProjectFilterOptionValues(
  rows: QuarantineQuotaRequestRow[],
  filterState: QuarantineQuotaRequestFilterState,
  projectNameById: QuarantineQuotaRequestProjectNameById,
  registeredProjectOptions: ProjectOption[],
): string[] {
  const rowsMatchingOtherFilters = rows.filter((row) => {
    for (const otherColumn of QUARANTINE_QUOTA_REQUEST_FILTER_COLUMNS) {
      if (otherColumn === "project") {
        continue;
      }

      const selectedValue = filterState[otherColumn];

      if (selectedValue === undefined || selectedValue === "") {
        continue;
      }

      if (
        getQuarantineQuotaRequestFilterCellValue(otherColumn, row, projectNameById) !==
        selectedValue
      ) {
        return false;
      }
    }

    return true;
  });

  const projectFilterValueSet = new Set<string>();

  for (const row of rowsMatchingOtherFilters) {
    projectFilterValueSet.add(resolveProjectSelectValue(row));
  }

  for (const registeredProjectOption of registeredProjectOptions) {
    projectFilterValueSet.add(registeredProjectOption.id);
  }

  const projectOptionsForSort: ProjectOption[] = [];

  for (const projectFilterValue of projectFilterValueSet) {
    if (projectFilterValue === PROJECT_SELECT_UNASSIGNED_VALUE) {
      continue;
    }

    const registeredProjectOption = registeredProjectOptions.find(
      (projectOption) => projectOption.id === projectFilterValue,
    );

    if (registeredProjectOption !== undefined) {
      projectOptionsForSort.push(registeredProjectOption);
    } else {
      projectOptionsForSort.push({
        id: projectFilterValue,
        name: projectNameById[projectFilterValue] ?? projectFilterValue,
      });
    }
  }

  const sortedProjectFilterValues = buildSortedProjectOptions(projectOptionsForSort).map(
    (projectOption) => projectOption.id,
  );

  if (projectFilterValueSet.has(PROJECT_SELECT_UNASSIGNED_VALUE)) {
    return [PROJECT_SELECT_UNASSIGNED_VALUE, ...sortedProjectFilterValues];
  }

  return sortedProjectFilterValues;
}

export function collectQuarantineQuotaRequestFilterOptions(
  rows: QuarantineQuotaRequestRow[],
  column: QuarantineQuotaRequestFilterColumn,
  filterState: QuarantineQuotaRequestFilterState,
  projectNameById: QuarantineQuotaRequestProjectNameById,
  registeredProjectOptions: ProjectOption[] = [],
): string[] {
  if (column === "project") {
    return collectProjectFilterOptionValues(
      rows,
      filterState,
      projectNameById,
      registeredProjectOptions,
    );
  }

  const rowsMatchingOtherFilters = rows.filter((row) => {
    for (const otherColumn of QUARANTINE_QUOTA_REQUEST_FILTER_COLUMNS) {
      if (otherColumn === column) {
        continue;
      }

      const selectedValue = filterState[otherColumn];

      if (selectedValue === undefined || selectedValue === "") {
        continue;
      }

      if (
        getQuarantineQuotaRequestFilterCellValue(otherColumn, row, projectNameById) !==
        selectedValue
      ) {
        return false;
      }
    }

    return true;
  });

  const distinctValues = new Set<string>();

  for (const row of rowsMatchingOtherFilters) {
    distinctValues.add(getQuarantineQuotaRequestFilterCellValue(column, row, projectNameById));
  }

  return [...distinctValues].sort((left, right) => left.localeCompare(right));
}

function compareOptionalNumbersAscending(
  leftValue: number | undefined,
  rightValue: number | undefined,
): number {
  if (leftValue === undefined && rightValue === undefined) {
    return 0;
  }

  if (leftValue === undefined) {
    return 1;
  }

  if (rightValue === undefined) {
    return -1;
  }

  return leftValue - rightValue;
}

function compareOptionalStringsAscending(leftValue: string | undefined, rightValue: string | undefined): number {
  const leftNormalized = leftValue ?? "";
  const rightNormalized = rightValue ?? "";

  return leftNormalized.localeCompare(rightNormalized);
}

export function compareQuarantineQuotaRequestRowsByColumn(
  column: QuarantineQuotaRequestSortColumn,
  left: QuarantineQuotaRequestRow,
  right: QuarantineQuotaRequestRow,
): number {
  switch (column) {
    case "requested_at": {
      const leftRequestedAtTimestamp = resolveRequestedAtEpochSeconds(left) ?? 0;
      const rightRequestedAtTimestamp = resolveRequestedAtEpochSeconds(right) ?? 0;

      return leftRequestedAtTimestamp - rightRequestedAtTimestamp;
    }
    case "deployment_environment_name":
      return left.deployment_environment_name.localeCompare(right.deployment_environment_name);
    case "aws_region":
      return left.aws_region.localeCompare(right.aws_region);
    case "quota_name":
      return resolveQuotaNameForRow(left).localeCompare(resolveQuotaNameForRow(right));
    case "aws_status":
      return left.aws_status.localeCompare(right.aws_status);
    case "requested_value":
      return compareOptionalNumbersAscending(left.requested_value, right.requested_value);
    case "project":
      return resolveProjectSelectValue(left).localeCompare(resolveProjectSelectValue(right));
    case "status":
      return left.status.localeCompare(right.status);
    case "priority":
      return compareOptionalStringsAscending(left.priority, right.priority);
    default:
      return 0;
  }
}

export function compareQuarantineQuotaRequestRows(
  left: QuarantineQuotaRequestRow,
  right: QuarantineQuotaRequestRow,
): number {
  const awsStatusCompare = left.aws_status.localeCompare(right.aws_status);

  if (awsStatusCompare !== 0) {
    return awsStatusCompare;
  }

  const environmentCompare = left.deployment_environment_name.localeCompare(
    right.deployment_environment_name,
  );

  if (environmentCompare !== 0) {
    return environmentCompare;
  }

  const regionCompare = left.aws_region.localeCompare(right.aws_region);

  if (regionCompare !== 0) {
    return regionCompare;
  }

  const leftRequestedAtTimestamp = resolveRequestedAtEpochSeconds(left) ?? 0;
  const rightRequestedAtTimestamp = resolveRequestedAtEpochSeconds(right) ?? 0;

  return leftRequestedAtTimestamp - rightRequestedAtTimestamp;
}

export type QuarantineQuotaRequestSortState = {
  column: QuarantineQuotaRequestSortColumn;
  direction: QuarantineQuotaRequestSortDirection;
};

export function sortQuarantineQuotaRequestRows(
  rows: QuarantineQuotaRequestRow[],
  sortState?: QuarantineQuotaRequestSortState,
): QuarantineQuotaRequestRow[] {
  const rowCopy = [...rows];

  if (sortState === undefined) {
    rowCopy.sort(compareQuarantineQuotaRequestRows);

    return rowCopy;
  }

  const directionMultiplier = sortState.direction === "asc" ? 1 : -1;

  rowCopy.sort((left, right) => {
    return directionMultiplier * compareQuarantineQuotaRequestRowsByColumn(sortState.column, left, right);
  });

  return rowCopy;
}

export function getTableRowBackgroundColorForAwsStatus(awsStatus: string): string | undefined {
  if (awsStatus === "CASE_CLOSED") {
    return "#fff0f0";
  }

  return undefined;
}
